import {
  ColDef,
  ColGroupDef,
  GridOptions,
  themeAlpine,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef } from "react";

import {
  IgAzurePrimitives,
  IgGrayPrimitives,
} from "@intelligems/ig-design-tokens/constants";
import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { DataType } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { ProfitAndLossHeaderRenderer } from "@src/features/analytics/tables/profitAndLossTable/ProfitAndLossHeaderRenderer";
import { useSelector } from "@src/store";
import { selectedVariations } from "@src/store/slices/variations";
import { theme } from "@src/theme";
import { UseQueryResult } from "@tanstack/react-query";

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
  rowBorder: `1px solid ${IgGrayPrimitives[600]}`,
  headerRowBorder: `1px solid ${IgGrayPrimitives[600]}`,
  headerFontWeight: 600,
  headerTextColor: IgGrayPrimitives["A300"],
  headerFontSize: "14px",
  wrapperBorder: `1px solid ${IgGrayPrimitives[600]}`,
  wrapperBorderRadius: "8px",
  borderRadius: "8px",
});

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .ag-header-row`]: {
    backgroundColor: IgAzurePrimitives[100],
  },
  [`& .ag-group-value`]: {
    fontSize: "14px",
    lineHeight: "16.8px",
  },
  "& .ag-header-cell:not(:first-of-type) .ag-header-cell-label": {
    justifyContent: "flex-end",
  },
  [`& .ag-header-cell-text`]: {
    color: `${theme.palette.text.secondary} !important`,
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "16.8px",
  },
  [`& .ag-icon`]: {
    display: "none",
  },

  [`& .ag-cell-wrapper.ag-row-group`]: {
    alignItems: "center !important",
  },
  [`& .ag-group-child-count`]: {
    display: "none",
  },
}));

interface ProfitAndLossTableProps {
  variationOverviewData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
}

export const ProfitAndLossTable = ({
  variationOverviewData,
}: ProfitAndLossTableProps) => {
  const theme = useTheme();
  const graphValueFormatter = useGraphValueFormatter();
  const variations = useSelector(selectedVariations);

  const gridRef = useRef<AgGridReact>(null);
  const gridOptions = useMemo<GridOptions>(() => {
    return {
      domLayout: "autoHeight",
      tooltipInteraction: true,
      headerHeight: 58,
      groupDefaultExpanded: -1,
      suppressAggFuncInHeader: true,
      groupDisplayType: "groupRows",
      groupRowRenderer: "agGroupCellRenderer",
    };
  }, []);
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      resizable: false,
      cellStyle: {
        fontSize: "14px",
        alignContent: "center",
        // color: ColorSecondary.main,
      },
      menuTabs: [],
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      sortable: false,
    };
  }, []);

  const filteredOverviewData = useMemo(() => {
    if (variationOverviewData && variationOverviewData[0]?.data?.metrics) {
      return variationOverviewData[0].data.metrics;
    }
    return [];
  }, [variationOverviewData]);

  const getVariationMetricFormattedValue = useCallback(
    (variationId: string, metricId: string, metricDataType: DataType) => {
      const foundVariationData = filteredOverviewData.find(
        (data) => data.variation_id === variationId
      );
      if (foundVariationData) {
        return graphValueFormatter({
          isAxisTick: false,
          value: foundVariationData[metricId]?.value || 0,
          configDataTypes: [metricDataType],
        });
      } else {
        return "";
      }
    },
    [filteredOverviewData]
  );

  const rowData = useMemo(() => {
    return [
      {
        category: "Product",
        metric: "Revenue",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "net_product_revenue",
              "currency"
            ),
          }),
          {}
        ),
      },
      {
        category: "Product",
        metric: "COGS",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "total_cogs",
              "currencyNoDecimal"
            ),
          }),
          {}
        ),
      },
      {
        category: "Product",
        metric: "Product Profit",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "product_profit",
              "currency"
            ),
          }),
          {}
        ),
        isProfit: true,
      },
      {
        category: "Product",
        metric: "% Margin",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "gross_margin_pct",
              "percentTwoDecimal"
            ),
          }),
          {}
        ),
        isItalic: true,
      },
      {
        category: "Shipping",
        metric: "Revenue",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "net_shipping_revenue",
              "currency"
            ),
          }),
          {}
        ),
      },
      {
        category: "Shipping",
        metric: "Cost",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "total_shipping_cost",
              "currency"
            ),
          }),
          {}
        ),
      },
      {
        category: "Shipping",
        metric: "Shipping Profit",
        isProfit: true,
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "shipping_profit",
              "currency"
            ),
          }),
          {}
        ),
      },
      {
        category: "Other",
        metric: "Transaction Fees",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "total_transaction_fee",
              "currency"
            ),
          }),
          {}
        ),
      },
      ...(variations.some((v) => {
        const raw =
          filteredOverviewData.find((d) => d.variation_id === v.id)?.[
            "total_tax_included"
          ]?.value || 0;
        return raw > 0;
      })
        ? [
            {
              category: "Other",
              metric: "Total Tax Included",
              ...variations.reduce(
                (acc, variation) => ({
                  ...acc,
                  [variation.id]: getVariationMetricFormattedValue(
                    variation.id,
                    "total_tax_included",
                    "currency"
                  ),
                }),
                {}
              ),
            },
          ]
        : []),
      {
        category: "Total",
        metric: "Revenue",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "net_revenue",
              "currency"
            ),
          }),
          {}
        ),
      },
      {
        category: "Total",
        metric: "Cost",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "total_cost",
              "currency"
            ),
          }),
          {}
        ),
      },
      {
        category: "Total",
        metric: "Total Profit",
        isProfit: true,
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "gross_profit",
              "currency"
            ),
          }),
          {}
        ),
      },
      {
        category: "Total",
        metric: "% Margin",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "gross_margin_pct",
              "percent"
            ),
          }),
          {}
        ),
      },
      {
        category: "Total",
        metric: "Visitors",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "n_visitors",
              "numberNoDecimal"
            ),
          }),
          {}
        ),
      },
      {
        category: "Total",
        metric: "Profit per Visitor",
        isProfit: true,
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "gross_profit_per_visitor",
              "currency"
            ),
          }),
          {}
        ),
      },
      {
        category: "Total",
        metric: "Orders",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "n_orders",
              "numberNoDecimal"
            ),
          }),
          {}
        ),
      },
      {
        category: "Total",
        metric: "Profit per Order",
        isProfit: true,
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "gross_profit_per_order",
              "currency"
            ),
          }),
          {}
        ),
      },
    ];
  }, [filteredOverviewData]);

  const getRowStyle = (params) => {
    if (params.node.data?.isProfit) {
      return { background: theme.igPrimitives.gray[300] };
    }
  };
  const columnDefs: (ColDef | ColGroupDef)[] = useMemo(() => {
    const blankGroup: (ColDef | ColGroupDef)[] = [
      {
        headerName: "",
        field: "category",
        rowGroup: true,
        hide: true,
      },
      {
        headerName: "",
        field: "metric",
        width: 150,
        cellStyle: (params) => {
          const style = {
            fontWeight: 400,
            fontStyle: "normal",
            fontSize: "14px",
            marginLeft: "30px",
          };
          if (params.data?.isProfit) {
            style.fontWeight = 600;
            // style.backgroundColor = ColorTextFill.hint;
          }
          if (params.data?.isItalic) {
            style.fontStyle = "italic";
          }
          return style;
        },
        cellRendererParams: {
          suppressCount: true,
        },
      },
    ];
    const variationGroups: ColDef[] = variations.map((variation) => {
      return {
        headerName: variation.name,
        field: variation.id,
        headerComponent: ProfitAndLossHeaderRenderer,
        cellStyle: (params) => {
          const style = {
            fontWeight: 400,
            fontStyle: "normal",
            textAlign: "right",
          };
          if (params.data?.isProfit) {
            style.fontWeight = 600;
          }
          if (params.data?.isItalic) {
            style.fontStyle = "italic";
          }
          return style;
        },
      };
    });
    return [...blankGroup, ...variationGroups];
  }, []);
  return (
    <StyledGrid
      container
      size={"grow"}
      direction={"column"}
      rowSpacing={2}
      sx={{ marginTop: "0px", width: "100%" }}
    >
      <AgGridReact
        ref={gridRef}
        theme={myTheme}
        loading={
          variationOverviewData[0].isLoading ||
          variationOverviewData[0].isPending
        }
        defaultColDef={defaultColDef}
        rowData={rowData}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        suppressDragLeaveHidesColumns={true}
        suppressExcelExport={true}
        suppressCsvExport={true}
        suppressContextMenu={true}
        suppressCellFocus={true}
        overlayLoadingTemplate={"<div>Loading Data... </div>"}
        overlayNoRowsTemplate={
          "<div>No Data - Your filters may have resulted in no data, try resetting them</div>"
        }
        headerHeight={38}
        suppressRowHoverHighlight={true}
        getRowStyle={getRowStyle}
      />
    </StyledGrid>
  );
};
